import { Link, Navigate } from "react-router-dom";
import { Users, CreditCard, Building2, Tags, FileText, Banknote, Settings, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

// Admin bookmarks — reached via UserDropdown → "Admin". Serves as the
// consolidated landing page after we removed Categories/Departments/
// Companies/All Expenses from the primary nav for admin (Phase 3a).
// Each card's `perm` key is the same permission the primary nav uses,
// so hiding an admin surface is a one-line change in
// `backend/core/permissions.py`.
const CARDS = [
  { title: "Users",         desc: "Manage user accounts, roles, defaults",       to: "/admin/users",              icon: Users,        perm: "nav.admin_landing" },
  { title: "Cards",         desc: "Company cards and card assignments",          to: "/admin/cards",              icon: CreditCard,   perm: "nav.admin_landing" },
  { title: "Categories",    desc: "Expense categories and mappings",             to: "/admin/categories",         icon: Tags,         perm: "nav.categories" },
  { title: "Departments",   desc: "Departments and cost centers",                to: "/admin/departments",        icon: Building2,    perm: "nav.departments" },
  { title: "Companies",     desc: "Legal entities and company codes",            to: "/admin/companies",          icon: Building2,    perm: "nav.companies" },
  { title: "All Expenses",  desc: "Cross-company expense list (all users)",      to: "/expenses",                 icon: FileText,     perm: "nav.all_expenses" },
  { title: "Plaid",         desc: "Connect and manage bank feeds",               to: "/admin/integrations/plaid", icon: Banknote,     perm: "nav.admin_landing" },
  { title: "Permissions",   desc: "Edit which roles see which nav items",        to: "/admin/permissions",        icon: ShieldCheck,  perm: "admin.manage_permissions" },
];


export default function AdminLanding() {
  const { user, hasPerm, loading } = useAuth();
  if (loading) return null;
  if (!user || !hasPerm("nav.admin_landing")) {
    return <Navigate to="/" replace />;
  }
  const cards = CARDS.filter((c) => hasPerm(c.perm));
  return (
    <div className="space-y-6" data-testid="admin-landing">
      <header className="flex items-center gap-3">
        <div className="rounded-md bg-[#0F1E3D]/5 p-2 text-[#0F1E3D]">
          <Settings className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Admin</h1>
          <p className="text-sm text-slate-500">Management surfaces for people, cards, and taxonomies.</p>
        </div>
      </header>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ title, desc, to, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            data-testid={`admin-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
            className="group rounded-md border border-slate-200 bg-white p-4 transition-colors hover:border-[#0F1E3D]/40 hover:bg-slate-50"
          >
            <div className="mb-2 flex items-center gap-2 text-slate-700 group-hover:text-[#0F1E3D]">
              <Icon className="h-4 w-4" />
              <h2 className="text-sm font-semibold">{title}</h2>
            </div>
            <p className="text-xs text-slate-500">{desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
